/**
 * DIASCompliancePage — combined Practice + Equipment + Staff licensing view.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck, Wrench, Users, FileBadge, ArrowRight, Loader2, Plus,
  Pencil, Trash2, X, Save,
} from 'lucide-react';
import Header from '../components/Header';
import BulkActions from '../components/BulkActions';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = '#3fa758';
const DARK = '#1f4a2e';
const MODALITIES = ['CT', 'Ultrasound', 'X-ray', 'DEXA', 'OPG', 'Fluoroscopy', 'MRI', 'Mammography', 'Other'];
const STATUSES = ['active', 'under_service', 'retired', 'decommissioned'];

export default function DIASCompliancePage() {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [tab, setTab] = useState('overview');
  const [equipment, setEquipment] = useState([]);
  const [equipStats, setEquipStats] = useState({});
  const [licensing, setLicensing] = useState({ summary: {}, users: [] });
  const [sopCompliance, setSopCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [eq, eqs, lic, sop] = await Promise.all([
        axios.get(`${API}/equipment`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/equipment/stats`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/users/licensing/expiring?within_days=60`, { withCredentials: true }).catch(() => ({ data: { summary: {}, users: [] } })),
        axios.get(`${API}/sops/compliance`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setEquipment(eq.data || []);
      setEquipStats(eqs.data || {});
      setLicensing(lic.data || { summary: {}, users: [] });
      setSopCompliance(sop.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const tabs = [
    { id: 'overview', l: 'Overview', Icon: ShieldCheck },
    { id: 'equipment', l: 'Equipment', Icon: Wrench },
    { id: 'staff', l: 'Staff licensing', Icon: Users },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="dias-compliance-page">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3" style={{ backgroundColor: `${ACCENT}1A`, color: ACCENT }}>
            <ShieldCheck className="w-3.5 h-3.5" />
            <span className="text-[10px] font-bold uppercase tracking-wider">DIAS · Practice + Equipment + Staff</span>
          </div>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ color: DARK }}>
            DIAS <span style={{ color: ACCENT }}>compliance</span>
          </h1>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
            Single view of everything an auditor will look at — practice policies, equipment service status, and staff licensing.
          </p>
        </div>

        <div className="flex items-center gap-1 flex-wrap mb-5" data-testid="dias-tabs">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`dias-tab-${t.id}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition border ${tab === t.id ? 'text-white border-transparent' : 'text-muted-foreground border-border hover:bg-muted'}`}
              style={tab === t.id ? { backgroundColor: ACCENT } : {}}>
              <t.Icon className="w-3 h-3" /> {t.l}
            </button>
          ))}
        </div>

        {loading ? <div className="bg-card border border-border rounded-2xl p-8 text-center text-sm text-muted-foreground">Loading…</div> : (
          <>
            {tab === 'overview' && <OverviewTab equipStats={equipStats} licensing={licensing} sopCompliance={sopCompliance} setTab={setTab} />}
            {tab === 'equipment' && <EquipmentTab equipment={equipment} stats={equipStats} isPM={isPM} onEdit={setEditing} onRefresh={refresh} />}
            {tab === 'staff' && <StaffLicensingTab licensing={licensing} />}
          </>
        )}
      </div>

      {editing && <EditEquipmentModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </div>
  );
}

function OverviewTab({ equipStats, licensing, sopCompliance, setTab }) {
  const lic = licensing.summary || {};
  return (
    <div className="space-y-4" data-testid="dias-overview">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SectionCard label="Equipment register" color="#0EA5E9" stats={[
          { l: 'Assets', v: equipStats.total || 0 },
          { l: 'Service due', v: equipStats.due_soon || 0, danger: equipStats.due_soon > 0 },
          { l: 'Overdue', v: equipStats.overdue_service || 0, danger: equipStats.overdue_service > 0 },
          { l: 'RSO required', v: equipStats.rso_required || 0 },
        ]} onClick={() => setTab('equipment')} linkLabel="Open register" />
        <SectionCard label="Staff licensing" color={ACCENT} stats={[
          { l: 'Current', v: lic.current || 0 },
          { l: 'Expiring 60d', v: lic.expiring_soon || 0, danger: lic.expiring_soon > 0 },
          { l: 'Expired', v: lic.expired || 0, danger: lic.expired > 0 },
          { l: 'Missing', v: lic.missing || 0, danger: lic.missing > 0 },
        ]} link="/people" linkLabel="Manage in People" />
        <SectionCard label="SOPs" color="#7C3AED" stats={[
          { l: 'Total', v: sopCompliance?.total_sops ?? '—' },
          { l: 'Acknowledged', v: sopCompliance?.total_acknowledged ?? '—' },
          { l: 'Coverage %', v: sopCompliance?.coverage_pct != null ? `${sopCompliance.coverage_pct}%` : '—' },
          { l: 'Outstanding', v: sopCompliance?.outstanding ?? '—', danger: (sopCompliance?.outstanding || 0) > 0 },
        ]} link="/compliance" linkLabel="Compliance hub" />
      </div>
      <div className="bg-card border border-border rounded-2xl p-5">
        <h3 className="font-['Outfit'] text-base font-bold mb-3 inline-flex items-center gap-2" style={{ color: DARK }}>
          <FileBadge className="w-4 h-4" /> Accreditation frameworks
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          {[
            ['DIAS — Diagnostic Imaging Accreditation Scheme', 'https://www.health.gov.au/our-work/diagnostic-imaging-accreditation-scheme'],
            ['ISO 9001 — Quality management', 'https://www.iso.org/iso-9001-quality-management.html'],
            ['RANZCR Standards of Practice', 'https://www.ranzcr.com'],
            ['Privacy Act 1988 (Cth)', 'https://www.oaic.gov.au/privacy/privacy-act/'],
          ].map(([label, url]) => (
            <a key={label} href={url} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-border hover:bg-muted">
              <span className="text-foreground">{label}</span><ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function SectionCard({ label, color, stats, onClick, link, linkLabel }) {
  const Inner = (
    <>
      <div className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color }}>{label}</div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {stats.map(s => (
          <div key={s.l}>
            <div className="text-[10px] uppercase text-muted-foreground tracking-wider">{s.l}</div>
            <div className={`text-2xl font-bold ${s.danger ? 'text-red-600' : ''}`} style={!s.danger ? { color: DARK } : {}}>{s.v}</div>
          </div>
        ))}
      </div>
      <span className="text-xs font-semibold inline-flex items-center gap-1" style={{ color }}>{linkLabel} <ArrowRight className="w-3 h-3" /></span>
    </>
  );
  if (onClick) return <button onClick={onClick} className="bg-card border border-border rounded-2xl p-4 text-left hover:bg-muted/30 w-full">{Inner}</button>;
  return <Link to={link} className="bg-card border border-border rounded-2xl p-4 hover:bg-muted/30 block">{Inner}</Link>;
}

function EquipmentTab({ equipment, stats, isPM, onEdit, onRefresh }) {
  const sorted = useMemo(() => [...equipment].sort((a, b) => (a.asset_name || '').localeCompare(b.asset_name || '')), [equipment]);
  return (
    <div className="space-y-3" data-testid="dias-equipment">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile l="Total assets" v={stats.total || 0} c="#0EA5E9" />
        <Tile l="Active" v={stats.by_status?.active || 0} c={ACCENT} />
        <Tile l="Service due ≤30d" v={stats.due_soon || 0} c="#D97706" />
        <Tile l="Overdue" v={stats.overdue_service || 0} c="#DC2626" />
      </div>
      <div className="flex items-center justify-end gap-2">
        <BulkActions resource="equipment" accent={ACCENT} writable={isPM} onImported={onRefresh} />
        {isPM && (
          <button data-testid="equipment-add" onClick={() => onEdit({ asset_name: '', modality: 'Ultrasound', status: 'active', components: [], document_urls: [] })}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl text-white" style={{ backgroundColor: ACCENT }}>
            <Plus className="w-4 h-4" /> Add equipment
          </button>
        )}
      </div>
      {sorted.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-8 text-center text-sm text-muted-foreground" data-testid="equipment-empty">
          No equipment recorded yet.
        </div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-muted/50 text-left text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                <th className="py-3 px-4">Asset</th><th className="py-3 px-3">Modality</th>
                <th className="py-3 px-3">Status</th><th className="py-3 px-3">Last service</th>
                <th className="py-3 px-3">Next due</th><th className="py-3 px-3">RSO</th>
                <th className="py-3 px-3 text-right">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-border">
                {sorted.map(e => (
                  <tr key={e.asset_id} data-testid={`equipment-row-${e.asset_id}`}>
                    <td className="py-3 px-4"><div className="font-semibold">{e.asset_name}</div><div className="text-xs text-muted-foreground">{e.manufacturer} {e.model}</div></td>
                    <td className="py-3 px-3 text-xs">{e.modality}</td>
                    <td className="py-3 px-3"><span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ backgroundColor: e.status === 'active' ? '#DCFCE7' : '#FEE2E2', color: e.status === 'active' ? '#166534' : '#991B1B' }}>{e.status}</span></td>
                    <td className="py-3 px-3 text-xs">{e.last_service_at || '—'}</td>
                    <td className="py-3 px-3 text-xs">{e.next_service_due || '—'}</td>
                    <td className="py-3 px-3 text-xs">{e.rso_required ? '✓' : '—'}</td>
                    <td className="py-3 px-3 text-right">{isPM && <button onClick={() => onEdit(e)} className="text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1 rounded border border-border hover:bg-muted"><Pencil className="w-3 h-3" /> Edit</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StaffLicensingTab({ licensing }) {
  const summary = licensing.summary || {};
  const rows = licensing.users || [];
  return (
    <div className="space-y-3" data-testid="dias-staff-licensing">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile l="Current" v={summary.current || 0} c="#16A34A" />
        <Tile l="Expiring 60d" v={summary.expiring_soon || 0} c="#D97706" />
        <Tile l="Expired" v={summary.expired || 0} c="#DC2626" />
        <Tile l="Missing data" v={summary.missing || 0} c="#6B7280" />
      </div>
      <div className="bg-card border border-border rounded-2xl p-4">
        <p className="text-sm text-muted-foreground">
          Detailed per-staff register lives in <Link to="/people" className="font-semibold underline" style={{ color: ACCENT }}>People &amp; licensing</Link>. {rows.length === 0 ? 'No clinical staff with a clinical role yet.' : `${rows.length} clinical staff currently in the register.`}
        </p>
      </div>
    </div>
  );
}

function Tile({ l, v, c }) {
  return (
    <div className="bg-card border border-border rounded-xl p-3.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: c }}>{l}</div>
      <div className="text-2xl font-bold" style={{ color: DARK }}>{v}</div>
    </div>
  );
}

function EditEquipmentModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({ ...initial }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isNew = !initial.asset_id;

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const payload = {
        asset_name: form.asset_name, modality: form.modality,
        manufacturer: form.manufacturer || '', model: form.model || '',
        serial_number: form.serial_number || '', location: form.location || '',
        install_date: form.install_date || '', warranty_expiry: form.warranty_expiry || '',
        last_service_at: form.last_service_at || '', next_service_due: form.next_service_due || '',
        last_calibration_at: form.last_calibration_at || '', next_calibration_due: form.next_calibration_due || '',
        rso_required: !!form.rso_required, radiation_licence_no: form.radiation_licence_no || '',
        radiation_licence_expiry: form.radiation_licence_expiry || '',
        status: form.status || 'active', components: form.components || [],
        notes: form.notes || '', document_urls: form.document_urls || [],
      };
      if (isNew) await axios.post(`${API}/equipment`, payload, { withCredentials: true });
      else await axios.put(`${API}/equipment/${initial.asset_id}`, payload, { withCredentials: true });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${initial.asset_name}?`)) return;
    setBusy(true);
    try { await axios.delete(`${API}/equipment/${initial.asset_id}`, { withCredentials: true }); onSaved(); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="equipment-edit-modal">
      <div className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-5 shadow-2xl space-y-4">
        <div className="flex items-start justify-between gap-3 pb-3 border-b border-border">
          <h3 className="font-['Outfit'] text-lg font-bold" style={{ color: DARK }}>{isNew ? 'Add equipment' : `Edit · ${initial.asset_name}`}</h3>
          <button onClick={onClose} disabled={busy}><X className="w-4 h-4" /></button>
        </div>
        {err && <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-2.5 text-xs text-red-600">{err}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Lbl l="Asset name *"><input data-testid="equipment-name" value={form.asset_name} onChange={e => setF('asset_name', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Lbl>
          <Lbl l="Modality *"><select value={form.modality} onChange={e => setF('modality', e.target.value)} data-testid="equipment-modality" className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background">{MODALITIES.map(m => <option key={m} value={m}>{m}</option>)}</select></Lbl>
          <Lbl l="Manufacturer"><input value={form.manufacturer || ''} onChange={e => setF('manufacturer', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Lbl>
          <Lbl l="Model"><input value={form.model || ''} onChange={e => setF('model', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Lbl>
          <Lbl l="Serial number"><input value={form.serial_number || ''} onChange={e => setF('serial_number', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background font-mono" /></Lbl>
          <Lbl l="Location"><input value={form.location || ''} onChange={e => setF('location', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Lbl>
          <Lbl l="Install date"><input type="date" value={form.install_date || ''} onChange={e => setF('install_date', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Lbl>
          <Lbl l="Warranty expiry"><input type="date" value={form.warranty_expiry || ''} onChange={e => setF('warranty_expiry', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Lbl>
          <Lbl l="Last service"><input type="date" value={form.last_service_at || ''} onChange={e => setF('last_service_at', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Lbl>
          <Lbl l="Next service due"><input type="date" value={form.next_service_due || ''} onChange={e => setF('next_service_due', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Lbl>
          <Lbl l="Status"><select value={form.status} onChange={e => setF('status', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background">{STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select></Lbl>
          <div className="flex items-center gap-2 pt-6">
            <input type="checkbox" id="rso" checked={!!form.rso_required} onChange={e => setF('rso_required', e.target.checked)} />
            <label htmlFor="rso" className="text-sm">RSO oversight required</label>
          </div>
        </div>
        {form.rso_required && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Lbl l="Radiation licence #"><input value={form.radiation_licence_no || ''} onChange={e => setF('radiation_licence_no', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Lbl>
            <Lbl l="Radiation licence expiry"><input type="date" value={form.radiation_licence_expiry || ''} onChange={e => setF('radiation_licence_expiry', e.target.value)} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Lbl>
          </div>
        )}
        <Lbl l="Notes"><textarea value={form.notes || ''} onChange={e => setF('notes', e.target.value)} rows={2} className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background" /></Lbl>
        <div className="flex items-center justify-between gap-2 pt-3 border-t border-border">
          {!isNew ? <button onClick={remove} disabled={busy} className="text-xs font-semibold text-red-600 inline-flex items-center gap-1 px-2.5 py-1.5 rounded border border-red-500/30 hover:bg-red-500/5"><Trash2 className="w-3 h-3" /> Delete</button> : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={busy} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
            <button onClick={submit} disabled={busy || !form.asset_name} data-testid="equipment-save"
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-5 py-2 rounded-lg text-white disabled:opacity-50"
              style={{ backgroundColor: ACCENT }}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {isNew ? 'Add' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Lbl({ l, children }) {
  return <div><label className="block text-xs font-medium mb-1">{l}</label>{children}</div>;
}
